
import { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useWishlist } from '../../hooks/useWishlist';
import { AuthGuardModal } from './AuthGuardModal';

interface WishlistButtonProps {
  propertyId: string;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'icon' | 'button';
}

export const WishlistButton = ({
  propertyId,
  className = '',
  size = 'md',
  variant = 'icon',
}: WishlistButtonProps) => {
  const { user } = useAuth();
  const { isInWishlist, toggleWishlist } = useWishlist();
  const [isAnimating, setIsAnimating] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);

  const isSaved = isInWishlist(propertyId);

  const sizeClasses = {
    sm: 'w-8 h-8 text-lg',
    md: 'w-10 h-10 text-xl',
    lg: 'w-12 h-12 text-2xl',
  };

  const handleClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!user) {
      setShowAuthModal(true);
      return;
    }

    try {
      setIsLoading(true);
      setIsAnimating(true);

      await toggleWishlist(propertyId);
    } catch (err) {
      console.error('Failed to toggle wishlist:', err);
    } finally {
      setIsLoading(false);
      setTimeout(() => setIsAnimating(false), 300);
    }
  };

  if (variant === 'button') {
    return (
      <>
        <button
          onClick={handleClick}
          disabled={isLoading}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all cursor-pointer whitespace-nowrap ${
            isSaved
              ? 'bg-rose-50 border-rose-200 text-rose-600 hover:bg-rose-100'
              : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
          } ${className}`}
        >
          <i
            className={`${isSaved ? 'ri-heart-fill' : 'ri-heart-line'} ${
              isAnimating ? 'animate-pulse scale-125' : ''
            }`}
          ></i>
          <span>{isSaved ? 'Saved' : 'Save'}</span>
        </button>
        <AuthGuardModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          title="Save to Wishlist"
          message="Sign in to save this property to your wishlist and access it anytime."
        />
      </>
    );
  }

  return (
    <>
      <button
        onClick={handleClick}
        disabled={isLoading}
        className={`${sizeClasses[size]} flex items-center justify-center rounded-full transition-all cursor-pointer ${
          isSaved
            ? 'bg-rose-500 text-white shadow-lg'
            : 'bg-white/90 text-gray-700 hover:bg-white shadow-md hover:shadow-lg'
        } ${isAnimating ? 'scale-110' : 'hover:scale-105'} ${className}`}
        title={isSaved ? 'Remove from wishlist' : 'Add to wishlist'}
      >
        {isLoading ? (
          <i className="ri-loader-4-line animate-spin"></i>
        ) : (
          <i
            className={`${isSaved ? 'ri-heart-fill' : 'ri-heart-line'} ${
              isAnimating ? 'animate-bounce' : ''
            }`}
          ></i>
        )}
      </button>
      <AuthGuardModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        title="Save to Wishlist"
        message="Sign in to save this property to your wishlist and access it anytime."
      />
    </>
  );
};
